import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@material-ui/core";
import styles from "./Scoreboard.module.scss";
import timeToString from '../../utils/timeToString';

// A submission that codeforces has not judged yet
const PENDING_VERDICTS = [null, undefined, "TESTING"];

// How long the flash of a submission that came back rejected takes to fade away.
// Keep in step with the judging-fade-out animation of the stylesheet.
const FADE_OUT_DURATION = 600;

// What each of the two contestants has done to a task. The cell of a task is
// split along the diagonal between them: contestant 1 owns the upper left half,
// contestant 2 the lower right one.
function taskSubmissionState(submissions, task, contestant1, contestant2) {
    const state = [contestant1, contestant2].map(() => ({
        submitted: false,
        judging: false,
        accepted: false
    }));
    (submissions ?? []).forEach(submission => {
        if (submission.contestId !== task.contestId || submission.taskIndex !== task.taskIndex) return;
        const contestant = submission.user === contestant1 ? 0 : (submission.user === contestant2 ? 1 : -1);
        if (contestant === -1) return;
        state[contestant].submitted = true;
        if (submission.verdict === "OK") state[contestant].accepted = true;
        else if (PENDING_VERDICTS.indexOf(submission.verdict) !== -1) state[contestant].judging = true;
    });
    // a solved task is settled - the first accepted submission claimed the cell,
    // so nothing left in the queue for it is worth flashing about
    state.forEach(contestantState => {
        if (contestantState.accepted || task.solvedUser != null) contestantState.judging = false;
    });
    return state;
}

// Flashes of submissions that have just been rejected, keyed by cell and
// contestant, so that they fade away instead of being cut off. A submission that
// came back accepted stops flashing at once: the cell it filled says it all.
function useRejectedFlashes(states) {
    const [fading, setFading] = useState({});
    const previousStates = useRef(null);
    const timeouts = useRef([]);
    useEffect(() => () => timeouts.current.forEach(clearTimeout), []);
    useEffect(() => {
        const previousState = previousStates.current;
        previousStates.current = states;
        if (previousState == null || states == null) return;
        const rejected = [];
        states.forEach((state, task) => state.forEach((contestantState, contestant) => {
            const wasJudging = previousState[task]?.[contestant]?.judging;
            if (wasJudging && !contestantState.judging && !contestantState.accepted) rejected.push(`${task}-${contestant}`);
        }));
        if (rejected.length === 0) return;
        setFading(fading => ({ ...fading, ...Object.fromEntries(rejected.map(key => [key, true])) }));
        timeouts.current.push(setTimeout(() => setFading(fading => {
            const remaining = { ...fading };
            rejected.forEach(key => delete remaining[key]);
            return remaining;
        }), FADE_OUT_DURATION));
    }, [states]);
    return fading;
}

// The half of the cell a contestant marks, and the colours it uses
const CONTESTANT_STYLES = [
    { half: "upperLeft", colors: "contestant1Colors" },
    { half: "lowerRight", colors: "contestant2Colors" }
];

// Every contestant that submitted to a task marks its two sides of the cell,
// whoever ends up solving it
function TaskMarks({ state }) {
    return state.map((contestantState, contestant) => {
        if (!contestantState.submitted) return null;
        const { half, colors } = CONTESTANT_STYLES[contestant];
        return <span key={contestant} className={`${styles["mark"]} ${styles[half]} ${styles[colors]}`} />;
    });
}

// A submission still being judged flashes over the cell, over half of it when
// both contestants are waiting for a verdict. One that has just been rejected
// keeps its half while it fades away.
function TaskFlashes({ state, fading, task }) {
    const showing = state.map((contestantState, contestant) => contestantState.judging || fading[`${task}-${contestant}`] === true);
    const bothShowing = showing[0] && showing[1];
    return state.map((contestantState, contestant) => {
        if (!showing[contestant]) return null;
        const { half, colors } = CONTESTANT_STYLES[contestant];
        const fadeOut = contestantState.judging ? "" : styles["fadeOut"];
        return (
            <span
                key={contestant}
                className={`${styles["flash"]} ${styles[colors]} ${bothShowing ? styles[half] : ""} ${fadeOut}`}
            />
        );
    });
}

function Scoreboard(props) {
    const states = useMemo(
        () => props.scoreboard?.map(task => taskSubmissionState(props.submissions, task, props.contestant1, props.contestant2)),
        [props.scoreboard, props.submissions, props.contestant1, props.contestant2]
    );
    const fading = useRejectedFlashes(states);
    return (
        <div className={styles["scoreboard"]}>
            <div className={styles["board"]}>
                {
                    props.scoreboard?
                        props.scoreboard.map((task, index) => {
                            const state = states[index];
                            return (
                                <a
                                    key={index}
                                    href={`https://codeforces.com/contest/${task.contestId}/problem/${task.taskIndex}`}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className={(
                                        task.solvedUser===props.contestant1?
                                            styles["contestant1"]
                                        :(
                                            task.solvedUser===props.contestant2?
                                                styles["contestant2"]
                                            :
                                                ""
                                        )
                                    )}
                                >
                                    <TaskFlashes state={state} fading={fading} task={index} />
                                    <TaskMarks state={state} />
                                    <span className={styles["label"]}>{task.contestId+task.taskIndex}</span>
                                    <span className={styles["label"]}>({task.difficulty})</span>
                                </a>
                            );
                        })
                        :new Array(9).fill(null).map((_value, index) => <div key={index}></div>)
                }
            </div>
            <div className={styles["legend"]}>
                <div>
                    <div className={styles["contestant1"]} />
                    <Link to={`/profile/${props.contestant1}`} className={styles["user"]}>
                        {props.contestant1}{(props.winner!=null&&props.contestant1===props.winner)?" (Winner)":""}
                    </Link>
                </div>
                <div>
                    <div className={styles["contestant2"]} />
                    <Link to={`/profile/${props.contestant2}`} className={styles["user"]}>
                        {props.contestant2}{(props.winner!=null&&props.contestant2===props.winner)?" (Winner)":""}
                    </Link>
                </div>
                <div>
                    <MatchConfigTable config={props.config} />
                </div>
            </div>
        </div>
    );
}

function MatchConfigTable(props) {
    return (
        <Card>
            <CardContent>
                <table className={styles["config"]}>
                    <tbody>
                        <tr>
                            <td>Difficulty</td>
                            <td>{props.config.difficulty}</td>
                        </tr>
                        <tr>
                            <td>Start Time</td>
                            <td>{props.config.startTime?timeToString(props.config.startTime):"—"}</td>
                        </tr>
                        <tr>
                            <td>Duration</td>
                            <td>{props.config.duration} minutes</td>
                        </tr>
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
}

export default Scoreboard;
